/**
 * Glasstunnel protocol - hand-written TypeScript shim.
 *
 * The source of truth for the protocol is `packages/protocol/schema/glasstunnel.proto`.
 * Run `pnpm --filter=@glasstunnel/protocol gen` to regenerate real protobuf bindings
 * once ts-proto is installed. In the meantime these hand-written types keep the rest
 * of the repo buildable and match the proto schema one-to-one.
 */
export const PROTOCOL_VERSION = '0.2.3';
export var AgentStatus;
(function (AgentStatus) {
    AgentStatus[AgentStatus["Unspecified"] = 0] = "Unspecified";
    AgentStatus[AgentStatus["Idle"] = 1] = "Idle";
    AgentStatus[AgentStatus["Working"] = 2] = "Working";
    AgentStatus[AgentStatus["WaitingInput"] = 3] = "WaitingInput";
    AgentStatus[AgentStatus["AwaitingApproval"] = 4] = "AwaitingApproval";
    AgentStatus[AgentStatus["Done"] = 5] = "Done";
    AgentStatus[AgentStatus["Error"] = 6] = "Error";
    AgentStatus[AgentStatus["Disconnected"] = 7] = "Disconnected";
})(AgentStatus || (AgentStatus = {}));
export var AdapterKind;
(function (AdapterKind) {
    AdapterKind[AdapterKind["Unspecified"] = 0] = "Unspecified";
    AdapterKind[AdapterKind["Mirror"] = 1] = "Mirror";
    AdapterKind[AdapterKind["Cursor"] = 2] = "Cursor";
    AdapterKind[AdapterKind["ClaudeCode"] = 3] = "ClaudeCode";
    AdapterKind[AdapterKind["CodexCli"] = 4] = "CodexCli";
    AdapterKind[AdapterKind["OpenCode"] = 5] = "OpenCode";
    AdapterKind[AdapterKind["Terminal"] = 6] = "Terminal";
    AdapterKind[AdapterKind["GeminiCli"] = 7] = "GeminiCli";
    AdapterKind[AdapterKind["CursorAgent"] = 8] = "CursorAgent";
    AdapterKind[AdapterKind["ClaudeDesktop"] = 9] = "ClaudeDesktop";
})(AdapterKind || (AdapterKind = {}));
export var ChatRole;
(function (ChatRole) {
    ChatRole[ChatRole["Unspecified"] = 0] = "Unspecified";
    ChatRole[ChatRole["User"] = 1] = "User";
    ChatRole[ChatRole["Assistant"] = 2] = "Assistant";
    ChatRole[ChatRole["System"] = 3] = "System";
    ChatRole[ChatRole["Tool"] = 4] = "Tool";
})(ChatRole || (ChatRole = {}));
export var QuickReplyKind;
(function (QuickReplyKind) {
    QuickReplyKind[QuickReplyKind["Unspecified"] = 0] = "Unspecified";
    QuickReplyKind[QuickReplyKind["Continue"] = 1] = "Continue";
    QuickReplyKind[QuickReplyKind["TryAgain"] = 2] = "TryAgain";
    QuickReplyKind[QuickReplyKind["Explain"] = 3] = "Explain";
    QuickReplyKind[QuickReplyKind["Commit"] = 4] = "Commit";
    QuickReplyKind[QuickReplyKind["Stop"] = 5] = "Stop";
    QuickReplyKind[QuickReplyKind["Approve"] = 6] = "Approve";
    QuickReplyKind[QuickReplyKind["Reject"] = 7] = "Reject";
})(QuickReplyKind || (QuickReplyKind = {}));
export var GridShape;
(function (GridShape) {
    GridShape[GridShape["Unspecified"] = 0] = "Unspecified";
    GridShape[GridShape["OneByOne"] = 1] = "OneByOne";
    GridShape[GridShape["TwoByOne"] = 2] = "TwoByOne";
    GridShape[GridShape["OneByTwo"] = 3] = "OneByTwo";
    GridShape[GridShape["TwoByTwo"] = 4] = "TwoByTwo";
})(GridShape || (GridShape = {}));
/** What a transcript record is, for rendering transcripts for reading. */
export var ChatMessageKind;
(function (ChatMessageKind) {
    ChatMessageKind[ChatMessageKind["Unspecified"] = 0] = "Unspecified";
    ChatMessageKind[ChatMessageKind["Text"] = 1] = "Text";
    ChatMessageKind[ChatMessageKind["ToolCall"] = 2] = "ToolCall";
    ChatMessageKind[ChatMessageKind["ToolResult"] = 3] = "ToolResult";
    ChatMessageKind[ChatMessageKind["Event"] = 4] = "Event";
})(ChatMessageKind || (ChatMessageKind = {}));
/** Wire protocol version. Even = stable, odd = development. */
export const CURRENT_PROTOCOL_VERSION = 4;
// =============================================================================
// RELAY TRANSPORT TYPES
// =============================================================================
export const RELAY_PATH = '/relay';
export const RELAY_PROTOCOL_VERSION = 1;
// =============================================================================
// JSON ENCODING/DECODING
// =============================================================================
export const encodeEnvelopeJson = (env) => JSON.stringify(env, bufferReplacer);
export const encodeDataChannelMessageJson = (msg) => JSON.stringify(msg, bufferReplacer);
export const decodeEnvelopeJson = (raw) => JSON.parse(raw);
export const decodeDataChannelMessageJson = (raw) => JSON.parse(raw);
/**
 * Canonical wire representation of binary fields: bare base64 strings.
 * Swift's JSONEncoder with `.base64` strategy and Go's `encoding/json`
 * unmarshalling into a `string` field both expect the same thing.
 *
 * Incoming envelopes (from Swift or Go) arrive with the Uint8Array-typed
 * fields as base64 strings; callers that need the raw bytes on the phone
 * can decode via `bytesFromBase64(field as unknown as string)`. In the
 * current PWA codebase nobody does, so we save a pass.
 */
function bufferReplacer(_key, value) {
    if (value instanceof Uint8Array) {
        return base64FromBytes(value);
    }
    return value;
}
export function base64FromBytes(bytes) {
    const anyGlobal = globalThis;
    if (anyGlobal.Buffer) {
        return anyGlobal.Buffer.from(bytes).toString('base64');
    }
    let binary = '';
    for (let i = 0; i < bytes.length; i++)
        binary += String.fromCharCode(bytes[i]);
    return anyGlobal.btoa ? anyGlobal.btoa(binary) : '';
}
export function bytesFromBase64(b64) {
    const anyGlobal = globalThis;
    if (anyGlobal.Buffer) {
        return new Uint8Array(anyGlobal.Buffer.from(b64, 'base64'));
    }
    const binary = anyGlobal.atob ? anyGlobal.atob(b64) : '';
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++)
        bytes[i] = binary.charCodeAt(i);
    return bytes;
}
// =============================================================================
// CONSTANTS
// =============================================================================
export const SIGNALING_PATH = '/signal';
export const SIGNALING_DEFAULT_PORT = 8080;
export const DEFAULT_SIGNALING_URL = 'wss://signaling.glasstunnel.io/signal';
export const DEFAULT_TURN_URL = 'turn:turn.glasstunnel.io:3478';
export const DEFAULT_STUN_URLS = [
    'stun:stun.l.google.com:19302',
    'stun:stun.cloudflare.com:3478',
];
//# sourceMappingURL=index.js.map